import { useState } from 'react';
import { Check } from 'assets/icons';
import Button from 'components/button';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import JournalErrorGroup from './JournalErrorGroup';

/** Grey blocks while the journal loads. */
export const JournalSkeleton = ({ label }) => (
  <div role="status" aria-label={label} className="flex flex-col gap-3">
    {[0, 1, 2].map((index) => (
      <div key={index} className="h-20 rounded-lg bg-gray-100 animate-pulse" />
    ))}
  </div>
);

/** "Le journal n'a pas pu être chargé" and a way to read it again. */
export const JournalLoadFailed = ({ text, onReload }) => (
  <div
    role="alert"
    className="flex flex-col items-center gap-3 py-16 text-center"
  >
    <div className="text-gray-700">
      {text('loadFailed', 'Le journal n’a pas pu être chargé')}
    </div>
    <Button color="SECONDARY" size="SM" onClick={onReload}>
      {text('reload', 'Recharger')}
    </Button>
  </div>
);

/**
 * The "À corriger" tab of the import journal (JBWEB-1280, mockup 2.8): the
 * month's invoices in error, one block per cause. The first block is unfolded.
 */
const JournalErrorsTab = ({
  errors,
  text,
  month,
  onRetry,
  isRetryDisabled,
  onViewInvoice,
}) => {
  // Codes whose fold the admin changed; every other block keeps its default.
  const [toggled, setToggled] = useState({});

  if (errors.status === 'loading') {
    return (
      <JournalSkeleton label={text('loading', 'Chargement du journal…')} />
    );
  }
  if (errors.status === 'failed') {
    return <JournalLoadFailed text={text} onReload={errors.reload} />;
  }

  const { total, groups } = errors.data;
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-16 text-center">
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
          <Check wh={20} color="#15803D" />
        </div>
        <div className="font-medium text-gray-900">
          {text('emptyTitle', 'Aucune facture à corriger')}
        </div>
        <div className="text-sm text-gray-500">
          {fillText(
            text('emptyText', 'Aucune facture de {month} n’est en erreur.'),
            {
              month,
            },
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-600">
        {total === 1
          ? text(
              'headerOne',
              '1 facture est en erreur. Corrigez sa cause, puis réessayez.',
            )
          : fillText(
              text(
                'headerMany',
                '{count} factures sont en erreur. Elles sont regroupées par cause : corrigez la cause, puis réessayez.',
              ),
              { count: total },
            )}
      </p>
      {groups.map((group, index) => {
        const isOpen = Boolean(toggled[group.code]) !== (index === 0);
        return (
          <JournalErrorGroup
            key={group.code}
            group={group}
            text={text}
            isOpen={isOpen}
            onToggle={() =>
              setToggled((previous) => ({
                ...previous,
                [group.code]: !previous[group.code],
              }))
            }
            onRetry={onRetry}
            isRetryDisabled={isRetryDisabled}
            onViewInvoice={onViewInvoice}
          />
        );
      })}
    </div>
  );
};

export default JournalErrorsTab;
